import { normalizePatternId, isBaseGradientPattern } from './cardPatterns';

function parseHex(hex) {
  const raw = (hex || '#4f6ef7').replace('#', '');
  if (raw.length === 3) {
    return raw.split('').map((c) => parseInt(c + c, 16));
  }
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

function toHex(r, g, b) {
  return `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function toRgba(hex, alpha = 1) {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function lightenColor(hex, amount = 0.25) {
  const [r, g, b] = parseHex(hex);
  return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

export function darkenColor(hex, amount = 0.2) {
  const [r, g, b] = parseHex(hex);
  return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

export function resolveGradientColor(mainColor, gradientColor) {
  if (gradientColor && /^#[0-9a-fA-F]{3,8}$/.test(gradientColor)) {
    return gradientColor;
  }
  return lightenColor(mainColor || '#4f6ef7', 0.38);
}

export function normalizeGradientAngle(angle) {
  const n = Number(angle);
  if (!Number.isFinite(n)) return 135;
  return ((Math.round(n) % 360) + 360) % 360;
}

function buildBaseGradientImage(pattern, mainColor, gradientColor, angle) {
  const from = mainColor || '#4f6ef7';
  const to = resolveGradientColor(from, gradientColor);
  const deg = normalizeGradientAngle(angle);

  if (pattern === 'gradient') {
    return `linear-gradient(${deg}deg, ${from} 0%, ${to} 100%)`;
  }

  if (pattern === 'mesh') {
    return [
      `radial-gradient(ellipse 90% 70% at 12% 8%, ${toRgba(to, 0.75)}, transparent 55%)`,
      `radial-gradient(ellipse 75% 60% at 92% 88%, ${toRgba(to, 0.55)}, transparent 52%)`,
      `radial-gradient(ellipse 55% 45% at 68% 35%, ${toRgba(from, 0.9)}, transparent 60%)`,
      `linear-gradient(${deg}deg, ${from} 0%, ${to} 100%)`,
    ].join(', ');
  }

  if (pattern === 'sunset') {
    const deep = darkenColor(to, 0.25);
    return `linear-gradient(${Math.max(deg, 145)}deg, ${from} 0%, ${to} 48%, ${deep} 100%)`;
  }

  return null;
}

export function getCardPatternCssVars({
  mainColor,
  backgroundPattern,
  gradientColor,
  gradientAngle,
}) {
  const color = mainColor || '#4f6ef7';
  const to = resolveGradientColor(color, gradientColor);
  return {
    '--open-card-bg': color,
    '--card-gradient-to': to,
    '--card-gradient-angle': `${normalizeGradientAngle(gradientAngle)}deg`,
  };
}

export function getCardBaseBackgroundStyle(opts) {
  const pattern = normalizePatternId(opts.backgroundPattern);
  const color = opts.mainColor || '#4f6ef7';
  const vars = getCardPatternCssVars(opts);

  if (!isBaseGradientPattern(pattern)) {
    return { backgroundColor: color, ...vars };
  }

  const backgroundImage = buildBaseGradientImage(
    pattern,
    color,
    opts.gradientColor,
    opts.gradientAngle,
  );

  return {
    backgroundColor: color,
    backgroundImage,
    ...vars,
  };
}

export function getPatternSwatchStyle(opts) {
  const pattern = normalizePatternId(opts.backgroundPattern);
  const color = opts.mainColor || '#4f6ef7';

  if (isBaseGradientPattern(pattern)) {
    return {
      backgroundColor: color,
      backgroundImage: buildBaseGradientImage(
        pattern,
        color,
        opts.gradientColor,
        opts.gradientAngle,
      ),
    };
  }

  return { backgroundColor: color };
}
